import { create } from "zustand"
import type { GenerationJobRow } from "@/types/workspace"

type WorkspaceJobsState = {
  projectId: number | null
  jobs: GenerationJobRow[]
  /** Authoritative window (navigation / project switch) — rows outside incoming are pruned, statuses forward-merged. */
  setJobs: (projectId: number, jobs: GenerationJobRow[]) => void
  /** Cable / partial reload — forward-only upsert, never rolls terminal back to active. */
  setJobsFromSnapshot: (projectId: number, jobs: GenerationJobRow[]) => void
  mergeJob: (job: GenerationJobRow) => void
}

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"])
const ACTIVE_STATUSES = new Set(["queued", "processing"])

function jobUpdatedAtMs(job: GenerationJobRow): number {
  const ts = job.updated_at
  return ts ? Date.parse(ts) : 0
}

function mergeJobRowForwardOnly(existing: GenerationJobRow, incoming: GenerationJobRow): GenerationJobRow {
  if (TERMINAL_STATUSES.has(existing.status) && ACTIVE_STATUSES.has(incoming.status)) {
    return existing
  }
  if (jobUpdatedAtMs(incoming) >= jobUpdatedAtMs(existing)) {
    return incoming
  }
  return existing
}

function forwardMergeIncoming(existing: GenerationJobRow[], incoming: GenerationJobRow[]): GenerationJobRow[] {
  const existingById = new Map(existing.map((j) => [j.id, j]))
  return incoming.map((j) => {
    const prev = existingById.get(j.id)
    return prev ? mergeJobRowForwardOnly(prev, j) : j
  })
}

// Снапшоты приходят с разными окнами (props контроллера — 50 строк, cable — 12),
// поэтому входящий список НЕ заменяет стор целиком: активная строка вне окна
// узкого снапшота «мигала» бы на каждом кабельном тике (например, активный джоб
// подготовки аниматика то появлялся, то исчезал — и страница аниматика
// перещёлкивалась между плеером и панелью «Готовим аниматик»). Известные строки
// обновляем forward-only, отсутствующие во входящем окне активные строки
// сохраняем; терминальные вне окна отпускаем (их дорисовывает история джобов).
export function mergeJobsSnapshot(existing: GenerationJobRow[], incoming: GenerationJobRow[]): GenerationJobRow[] {
  const incomingIds = new Set(incoming.map((j) => j.id))
  const merged = forwardMergeIncoming(existing, incoming)
  const keptOutsideWindow = existing.filter((j) => !incomingIds.has(j.id) && ACTIVE_STATUSES.has(j.status))
  return keptOutsideWindow.length > 0 ? [...merged, ...keptOutsideWindow] : merged
}

function mergeJobRow(rows: GenerationJobRow[], job: GenerationJobRow): GenerationJobRow[] {
  const idx = rows.findIndex((r) => r.id === job.id)
  if (idx >= 0) {
    const next = [...rows]
    next[idx] = job
    return next
  }
  return [job, ...rows]
}

export const useWorkspaceJobsStore = create<WorkspaceJobsState>((set) => ({
  projectId: null,
  jobs: [],
  // Окно = входящий список (прополка накопленных строк), но статусы известных
  // джобов forward-merged: history restore отдаёт props многоминутной давности,
  // и без merge завершившийся джоб откатывался бы в active.
  setJobs: (projectId, jobs) =>
    set((state) => {
      // Don't wipe an existing window with an empty payload (missing props / failed partial reload).
      if (jobs.length === 0 && state.projectId === projectId && state.jobs.length > 0) {
        return state
      }
      return {
        projectId,
        jobs: state.projectId === projectId ? forwardMergeIncoming(state.jobs, jobs) : jobs,
      }
    }),
  setJobsFromSnapshot: (projectId, jobs) =>
    set((state) => ({
      projectId,
      jobs: state.projectId === projectId ? mergeJobsSnapshot(state.jobs, jobs) : jobs,
    })),
  mergeJob: (job) =>
    set((state) => ({
      jobs: mergeJobRow(state.jobs, job),
    })),
}))

export function selectActiveBulkJob(animaticId: number | null) {
  return (state: WorkspaceJobsState): GenerationJobRow | null => {
    if (!animaticId) return null
    return (
      state.jobs.find(
        (j) =>
          j.job_kind === "animatic_bulk_segments" &&
          j.subject_type === "EpisodeAnimatic" &&
          j.subject_id === animaticId &&
          (j.status === "queued" || j.status === "processing"),
      ) ?? null
    )
  }
}

/** Latest bulk job for animatic (any terminal status) — for result banner after completion. */
export function selectLatestBulkJob(animaticId: number | null) {
  return (state: WorkspaceJobsState): GenerationJobRow | null => {
    if (!animaticId) return null
    return (
      state.jobs.find(
        (j) =>
          j.job_kind === "animatic_bulk_segments" &&
          j.subject_type === "EpisodeAnimatic" &&
          j.subject_id === animaticId,
      ) ?? null
    )
  }
}

/** Narrow selector: re-renders only when active bulk job appears/disappears or changes terminal status. */
export function selectActiveBulkJobKey(animaticId: number | null) {
  return (state: WorkspaceJobsState): string | null => {
    const job = selectActiveBulkJob(animaticId)(state)
    return job ? `${job.id}:${job.status}` : null
  }
}

/** Active (or latest) canvas_generate / canvas sketch_edit job for a canvas generation tile/modal. */
export function selectCanvasGenerationJob(generationId: number | null) {
  return (state: WorkspaceJobsState): GenerationJobRow | null => {
    if (generationId == null) return null
    const matches = state.jobs.filter(
      (j) =>
        j.canvas_generation_id === generationId && (j.job_kind === "canvas_generate" || j.job_kind === "sketch_edit"),
    )
    if (matches.length === 0) return null
    return matches.find((j) => ACTIVE_STATUSES.has(j.status)) ?? matches[0] ?? null
  }
}
